import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Building2, Plus, Check, ChevronDown, Settings } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteSelector = ({ onSiteChange }) => {
  const { token, user } = useAuth();
  const [sites, setSites] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.is_super_admin) {
      fetchSites();
    }
    fetchCurrentSite();
  }, [token, user]);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSite = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sites/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSite(data);
      }
    } catch (error) {
      console.error('Error fetching current site:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchSite = async (siteId) => {
    try {
      const response = await fetch(`${API_URL}/api/sites/switch/${siteId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Switched to ${data.site_name}`);
        fetchCurrentSite();
        setIsOpen(false);
        if (onSiteChange) onSiteChange(siteId);
      }
    } catch (error) {
      toast.error('Error switching site');
    }
  };

  if (loading || !currentSite) {
    return (
      <div className="px-4 py-3 bg-stone-700/50 rounded-lg animate-pulse">
        <div className="h-5 bg-stone-600 rounded w-32"></div>
      </div>
    );
  }

  // If not super admin, just show current site name
  if (!user?.is_super_admin) {
    return (
      <div className="px-4 py-3 bg-stone-700/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-terracota" />
          <span className="text-white text-sm font-medium">{currentSite.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-stone-700/50 hover:bg-stone-700 rounded-lg transition-colors flex items-center justify-between"
        data-testid="site-selector-btn"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-terracota" />
          <span className="text-white text-sm font-medium truncate max-w-[140px]">
            {currentSite.name}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 right-0 mt-2 bg-stone-800 border border-stone-700 rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="p-2 border-b border-stone-700">
              <p className="text-xs text-stone-400 uppercase tracking-wider px-2">Switch Site</p>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {sites.map((site) => (
                <button
                  key={site.id}
                  onClick={() => switchSite(site.id)}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-stone-700/50 transition-colors ${
                    site.id === currentSite.id ? 'bg-stone-700/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: site.theme?.primary_color || '#C4785A' }}
                    >
                      {site.name.charAt(0)}
                    </div>
                    <div className="text-left">
                      <p className="text-white text-sm font-medium">{site.name}</p>
                      <p className="text-stone-500 text-xs">{site.slug}</p>
                    </div>
                  </div>
                  {site.id === currentSite.id && (
                    <Check className="w-4 h-4 text-terracota" />
                  )}
                </button>
              ))}
            </div>
            <div className="p-2 border-t border-stone-700">
              <a
                href="/admin/sites"
                className="w-full px-4 py-2 flex items-center gap-2 text-terracota hover:bg-stone-700/50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Create New Site</span>
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteSelector;
